import type { MutateOptions, UseMutationOptions } from "@tanstack/react-query";

export class ApiSessionEndedError extends Error {
  constructor() {
    super("The API session has ended");
    this.name = "ApiSessionEndedError";
  }
}

export type SessionMutationVariables<TVariables> = { session: number; variables: TVariables };

/** Keep the session with the invocation, including while it is paused or retried. */
export function createSessionMutation<TData, TVariables, TContext>(
  getSession: () => number,
  call: (variables: TVariables) => Promise<TData>,
  options: Omit<UseMutationOptions<TData, Error, TVariables, TContext>, "mutationFn">
) {
  type Invocation = SessionMutationVariables<TVariables>;
  const isCurrent = (invocation: Invocation) => invocation.session === getSession();
  const assertCurrent = (invocation: Invocation) => {
    if (!isCurrent(invocation)) throw new ApiSessionEndedError();
  };
  const mutationOptions: UseMutationOptions<TData, Error, Invocation, TContext> = {
    ...options,
    retry: (failureCount, error) => {
      if (error instanceof ApiSessionEndedError) return false;
      const retry = options.retry ?? false;
      return typeof retry === "function" ? retry(failureCount, error) : retry === true || (typeof retry === "number" && failureCount < retry);
    },
    mutationFn: async invocation => {
      assertCurrent(invocation);
      try {
        const data = await call(invocation.variables);
        assertCurrent(invocation);
        return data;
      } catch (error) {
        assertCurrent(invocation);
        throw error;
      }
    },
    onMutate: options.onMutate
      ? (invocation, context) => {
          assertCurrent(invocation);
          return options.onMutate!(invocation.variables, context);
        }
      : undefined,
    onSuccess: (data, invocation, result, context) => {
      if (isCurrent(invocation)) return options.onSuccess?.(data, invocation.variables, result, context);
    },
    onError: (error, invocation, result, context) => {
      if (isCurrent(invocation)) return options.onError?.(error, invocation.variables, result, context);
    },
    onSettled: (data, error, invocation, result, context) => {
      if (isCurrent(invocation)) return options.onSettled?.(data, error, invocation.variables, result, context);
    },
  };
  const invocation = (variables: TVariables): Invocation => ({ session: getSession(), variables });
  const callbacks = (supplied?: MutateOptions<TData, Error, TVariables, TContext>): MutateOptions<TData, Error, Invocation, TContext> => ({
    onSuccess: (data, invocation, result, context) => {
      if (isCurrent(invocation)) supplied?.onSuccess?.(data, invocation.variables, result, context);
    },
    onError: (error, invocation, result, context) => {
      if (isCurrent(invocation)) supplied?.onError?.(error, invocation.variables, result, context);
    },
    onSettled: (data, error, invocation, result, context) => {
      if (isCurrent(invocation)) supplied?.onSettled?.(data, error, invocation.variables, result, context);
    },
  });
  // Hook callbacks may be async; the session can end while TanStack awaits them.
  const settle = async (promise: Promise<TData>, invocation: Invocation) => {
    try {
      const data = await promise;
      assertCurrent(invocation);
      return data;
    } catch (error) {
      assertCurrent(invocation);
      throw error;
    }
  };
  return { mutationOptions, invocation, callbacks, settle };
}
